import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { sort } from 'fast-sort';
import { NGXLogger } from 'ngx-logger';
import { MenuItem } from 'primeng/api';
import { I18nViewModel } from 'projects/sigv-library/src/model/src/public-api';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import UrlJoin from 'url-join';
import { Company } from '../../../model/src/lib/company.model';
import { FunctionPrivilege } from '../../../model/src/lib/function-privilege.model';
import { SystemFunction } from '../../../model/src/lib/system-function.model';
import { System } from '../../../model/src/lib/system.model';
import { isNullOrEmpty } from '../../../util/src/lib/is-null-or-empty';
import { Configuration } from '../../config/configuration';
import { ApiRbacService } from './api-rbac.service';
import { AuthenticationService } from './authentication.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private sidenav$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  private menus$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>(
    []
  );

  private companyDataFromMenu: Company[] = [];
  private companys$: BehaviorSubject<Company[]> = new BehaviorSubject<
    Company[]
  >([]);

  private rbacEnvSystem$: BehaviorSubject<System[]> = new BehaviorSubject<
    System[]
  >([]);

  constructor(
    private apiRbacService: ApiRbacService,
    private configuration: Configuration,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private logger: NGXLogger
  ) {
    if (this.configuration.initMenu) {
      this.authenticationService.currentUser.subscribe((userProfile) => {
        if (!isNullOrEmpty(userProfile) && userProfile?.userNo) {
          this.initMenuData(userProfile.userNo);
        }
      });
    }

    this.translateService.onLangChange.subscribe((result: LangChangeEvent) => {
      if (this.menus$.value.length > 0) {
        const transMenus = this.translateMenuItemLabel(
          this.menus$.value,
          result.lang
        );
        this.logger.info('MenuService - translated - menus', transMenus);
        this.menus$.next(transMenus);
      }
    });
  }

  getMenuItemsObservable(): Observable<MenuItem[]> {
    return this.menus$.asObservable();
  }

  getSideMenuObservable(): Observable<boolean> {
    return this.sidenav$.asObservable();
  }

  getCompanysFromMenuTreesObservable(): Observable<Company[]> {
    return this.companys$.asObservable();
  }

  toggleSideMenu(): void {
    this.sidenav$.next(!this.sidenav$.value);
  }

  getRbacSystemList(): Observable<System[]> {
    return this.rbacEnvSystem$.asObservable();
  }

  initMenuData(userNo: string): void {

    const systemGroup = this.storageService.session.getItem('systemGroup') ?? '';
    // Todo: 測試完後下次更新移除
    console.log('MenuService initMenuData systemGroup:', systemGroup);

    const systemGroupCode = !isNullOrEmpty(systemGroup) 
      ? systemGroup
      : this.configuration.systemGroupCode;
    // 取得使用者選單資料
    const getMenuTree$ = this.apiRbacService.getMenuTrees(
      userNo,
      systemGroupCode
    );
    // 取得 Rbac 所有系統設定資料
    const getEnvSystems$ = this.apiRbacService.getSystems();

    // 清空menu
    this.menus$.next([]);

    forkJoin([getMenuTree$, getEnvSystems$]).subscribe(
      ([menuTree, envSystems]) => {
        this.rbacEnvSystem$.next(envSystems);
        // 依據模組常數設定設定的 system code 之資料，用來確認是否為此系統所屬的功能
        const currentSystems = envSystems.filter((x) =>
          this.configuration.systemCodes.includes(x.systemCode)
        );
        if (menuTree && menuTree.length > 0) {
          const menus: MenuItem[] = [];
          const companys = menuTree.map((item): Company => {
            return {
              companyId: item.companyId.toString(),
              companyCode: item.companyCode,
              companyName: item.companyName,
              companyShortName: item.companyShortName,
            };
          });
          this.companyDataFromMenu = companys;
          this.companys$.next(companys);
          menuTree.forEach((company) => {
            const systemLayer: MenuItem[] = [];
            const sortSystems = sort(company.systems ?? []).asc(
              (x) => x.sequence
            );
            this.logger.info('sortSystems: ', sortSystems);
            sortSystems.forEach((system) => {
              const functionLayer: MenuItem[] = [];
              const currentSystem = currentSystems.find(
                (item) => item.systemCode === system.systemCode
              );

              const sortSystemFunctions = sort(
                system.systemFunctions ?? []
              ).asc((x) => x.sequence);
              this.logger.info('sortSystemFunctions: ', sortSystemFunctions);
              sortSystemFunctions.forEach((systemFunction) => {
                const functionPrivilegeLayer: MenuItem[] = [];
                // for only single function privilege item
                // not show level 4 item, click level 3 item navigate to first function privilege path
                if (
                  systemFunction.functionPrivileges &&
                  systemFunction.functionPrivileges.length === 1
                ) {
                  const menuItem: MenuItem = {
                    styleClass: 'p-panelmenu-list-level-3',
                  };
                  const privilegeItem = systemFunction.functionPrivileges[0];

                  menuItem.label = this.doTranslate(
                    {
                      defaultLabel: privilegeItem.privilegeNameEnglish ?? privilegeItem.privilegeName,
                      i18nLabels: privilegeItem.privilegeNameOther ?? []
                    },
                    this.translateService.currentLang
                  );
                  menuItem.state = {
                    defaultLabel: privilegeItem.privilegeNameEnglish ?? privilegeItem.privilegeName,
                    i18nLabels: privilegeItem.privilegeNameOther ?? []
                  };
                  menuItem.command = (event: any) => {
                    this.commandAction(
                      company.companyId.toString(),
                      {
                        system,
                        systemFunction,
                        privilegeItem,
                      },
                      currentSystem
                    );
                  };

                  functionLayer.push(menuItem);
                } else {
                  const sortiFunctionPrivileges = sort(
                    systemFunction.functionPrivileges ?? []
                  ).asc((x) => x.sequence);
                  this.logger.info(
                    'sortiFunctionPrivileges: ',
                    sortiFunctionPrivileges
                  );

                  // default rule, show level 4 item
                  sortiFunctionPrivileges.forEach((privilegeItem) => {
                    const menuItem: MenuItem = {
                      label: this.doTranslate(
                        {
                          defaultLabel: privilegeItem.privilegeNameEnglish ?? privilegeItem.privilegeName,
                          i18nLabels: privilegeItem.privilegeNameOther ?? []
                        },
                        this.translateService.currentLang
                      ),
                      styleClass: 'p-panelmenu-list-level-4',
                      state: {
                        name: privilegeItem.privilegeName,
                        defaultLabel: privilegeItem.privilegeNameEnglish ?? privilegeItem.privilegeName,
                        i18nLabels: privilegeItem.privilegeNameOther ?? []
                      },
                    };

                    menuItem.command = (event: any) => {
                      this.commandAction(
                        company.companyId.toString(),
                        {
                          system,
                          systemFunction,
                          privilegeItem,
                        },
                        currentSystem
                      );
                    };

                    functionPrivilegeLayer.push(menuItem);
                  });

                  functionLayer.push({
                    label: this.doTranslate(
                      {
                        defaultLabel: systemFunction.functionNameEnglish ?? systemFunction.functionName,
                        i18nLabels: systemFunction.functionNameOther ?? []
                      },
                      this.translateService.currentLang
                    ),
                    styleClass: 'p-panelmenu-list-level-3',
                    items: functionPrivilegeLayer,
                    state: {
                      name: systemFunction.functionName,
                      defaultLabel: systemFunction.functionNameEnglish ?? systemFunction.functionName,
                      i18nLabels: systemFunction.functionNameOther ?? []
                    },
                  });
                }
              });

              systemLayer.push({
                label: this.doTranslate(
                  {
                    defaultLabel: system.systemNameEnglish ?? system.systemName,
                    i18nLabels: system.systemNameOther ?? []
                  },
                  this.translateService.currentLang
                ),
                styleClass: 'p-panelmenu-list-level-2',
                items: functionLayer,
                expanded: true,
                state: {
                  name: system.systemName,
                  defaultLabel: system.systemNameEnglish ?? system.systemName,
                  i18nLabels: system.systemNameOther ?? []
                },
              });
            });

            menus.push({
              id: company.companyId.toString(),
              label: company.companyShortName,
              items: systemLayer,
              expanded: true,
            });
          });
          this.menus$.next(menus);
        }
      }
    );
  }

  /**
   * @description 使用公司代碼取得公司資料, 資料來源 rbac menu tree api
   * (未來會移除，可改用 getCompanysFromMenuTreesObservable 取得公司資料，避免重新整理無法取得資料問題)
   * @param id 公司代碼
   * @returns 公司資料
   */
  getCompanyDataById(id: string): Company | null {
    if (id) {
      const target = this.companyDataFromMenu.find(
        (item) => item.companyId === id
      );
      if (target) {
        return target;
      }
    }

    return null;
  }

  /**
   * @description for same path page reload
   * @param routerLink 要前往的路由設定
   */
  private navigateTo(routerLink: string[]): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(routerLink);
    });
  }

  /**
   * @description (遞迴)翻譯選單顯示文字, 產生新的選單資料物件
   * @param menus 選單資料
   * @returns 翻譯後的選單資料
   */
  private translateMenuItemLabel(
    menus: MenuItem[],
    currentLanguage: string
  ): MenuItem[] {
    const newMenuItems: MenuItem[] = [];

    if (menus && menus.length > 0) {
      menus.forEach((item) => {
        const { items, ...others } = item;
        const cloneMenuItem: MenuItem = {
          ...others,
        };
        if (!isNullOrEmpty(item.state)) {
          cloneMenuItem.label = this.doTranslate(
            {
              defaultLabel: item.state?.defaultLabel,
              i18nLabels: item.state?.i18nLabels,
            },
            currentLanguage
          );
        }

        if (items) {
          cloneMenuItem.items = this.translateMenuItemLabel(
            items,
            currentLanguage
          );
        }
        newMenuItems.push(cloneMenuItem);
      });
    }

    return newMenuItems;
  }

  /**
   * @description 依據傳入之語系資料與當前語系回傳選單應顯示文字
   * @param translateData 翻譯資料，包含英文、本地語言資料
   * @param currentLanguage 當前語系
   * @returns 選單對應的語系文字
   */
  private doTranslate(
    translateData: { defaultLabel: string, i18nLabels: I18nViewModel[] },
    currentLanguage: string
  ): string {
    const label = (translateData.i18nLabels.find(x => x.i18nCode === currentLanguage)?.i18nValue)
      ?? translateData.defaultLabel;
    return label;
  }

  /**
   * @description 選單點擊連結後，執行的動作
   * @param companyId 公司代碼
   * @param menuSource 選單相關資料 System, SystemFunction, FunctionPrivilege
   * @param currentSystem hosting system 資料
   */
  private commandAction(
    companyId: string,
    menuSource: {
      system: System;
      systemFunction: SystemFunction;
      privilegeItem: FunctionPrivilege;
    },
    currentSystem?: System
  ): void {
    if (
      menuSource.system.systemHostUrl &&
      menuSource.system.systemHostUrl === currentSystem?.systemHostUrl
    ) {
      if (menuSource.privilegeItem.entryUrl) {
        const routerLink = [companyId];
        const paths = menuSource.privilegeItem.entryUrl
          .split('/')
          .filter((item) => !!item);
        if (paths && paths.length > 0) {
          routerLink.push(...paths);
        }
        this.navigateTo(routerLink);
      } else {
        this.logger.error(
          `${menuSource.system.systemCode} - ${menuSource.systemFunction.functionCode} - ${menuSource.privilegeItem.privilegeCode} entryUrl is null`
        );
      }
    } else {
      const { functionCode, processDefKey, systemFunctionId } =
        menuSource.systemFunction;
      const queryParams = new HttpParams()
        .set('lang', this.translateService.currentLang)
        .set('companyId', companyId)
        .set('systemId', menuSource.system.systemId?.toString())
        .set('systemFunctionId', systemFunctionId?.toString())
        .set('processDefKey', processDefKey ? processDefKey.toString() : '')
        .set('functionCode', functionCode?.toString())
        .set('privilegeCode', menuSource.privilegeItem.privilegeCode?.toString())
        .set('grantExternalAccess', 'true');

      const url = `${UrlJoin(
        menuSource.system.systemHostUrl,
        menuSource.privilegeItem.entryUrl
          ? menuSource.privilegeItem.entryUrl
          : ''
      )}?${queryParams.toString()}`;
      const urlExpression =
        /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
      const urlRegex = new RegExp(urlExpression);
      if (url.match(urlRegex)) {
        window.location.href = url;
      } else {
        this.logger.error(
          `[${url}] is not a valid URL, systemHostUrl should be domain url.`
        );
      }
    }
  }
}
